import React from "react";
import { useTranslation } from "react-i18next";
import { map } from "lodash";
import Text from "@/components/ui/text";
import KPIsContainer from "@/components/controls/KPIsContainer";
import {
  ContractData,
  ENABLE_ONBOARDING_PHASE_KEY
} from "@/pages/contracts/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useBooleanAccountSetting } from "@/entities/settings/useBooleanAccountSetting";
import { mockAiRenewalProbability } from "@/assets/mockupData";
import AiRenewalProbabilityKPI from "@/pages/contracts/components/ContractKPIs/AiRenewalProbabilityKPI";
import RenewalConfidenceKPI from "@/pages/contracts/components/ContractKPIs/RenewalConfidenceKPI";
import OnboardingStatusKPI from "@/pages/contracts/components/ContractKPIs/OnboardingStatusKPI";
import NextRenewalKPI from "@/pages/contracts/components/ContractKPIs/NextRenewalKPI";
import RenewalBookKPI from "@/pages/contracts/components/ContractKPIs/RenewalBookKPI";
import { ContractFieldUpdate } from "@/pages/contracts/ContractDetailedView/ContractDetailedView";

interface Props {
  contract?: ContractData;
  containerClassName?: string;
  isQuickView?: boolean;
  onUpdateField: ContractFieldUpdate;
}

const ContractKPIs: React.FC<Props> = ({
  contract,
  containerClassName,
  isQuickView,
  onUpdateField
}) => {
  const { t } = useTranslation();
  const { enabled: isContractKpisEnabled, loading: isFlagLoading } =
    useFeatureFlag(FeatureFlags.CRM_7756_CONTRACT_KPIS);
  // `undefined` until the setting loads, so the onboarding tile never shows
  // and then disappears for an account that turned onboarding off.
  const onboardingEnabled =
    useBooleanAccountSetting(ENABLE_ONBOARDING_PHASE_KEY).value === true;
  // Read the demo flag's own loading state too — resolving it after the KPI row
  // has rendered would insert a tile at the front and shift the whole row.
  const { enabled: isDemo, loading: isDemoFlagLoading } = useFeatureFlag(
    FeatureFlags.CRM_6987_DEMO_MODE
  );

  if (!contract || isFlagLoading || isDemoFlagLoading) return null;
  if (!isContractKpisEnabled) return null;

  const valueVariant = isQuickView ? "lgLabel" : "lgSubtitleH1";

  const kpis: { key: string; subtitle: string; content: JSX.Element }[] = [
    // Demo only: mocked AI-derived probability, shown ahead of the owner's own
    // confidence so the two read side by side.
    ...(isDemo
      ? [
          {
            key: "aiRenewalProbability",
            subtitle: t("contracts.kpis.aiRenewalProbability"),
            content: (
              <AiRenewalProbabilityKPI
                value={mockAiRenewalProbability(contract.id)}
                valueVariant={valueVariant}
              />
            )
          }
        ]
      : []),
    {
      key: "renewalConfidence",
      subtitle: t("contracts.kpis.renewalConfidence"),
      content: (
        <RenewalConfidenceKPI
          value={contract.csmRenewalConfidenceLevel}
          valueVariant={valueVariant}
          onUpdateField={onUpdateField}
        />
      )
    },
    // Onboarding status only surfaces when onboarding is enabled in settings.
    ...(onboardingEnabled
      ? [
          {
            key: "onboardingStatus",
            subtitle: t("contracts.kpis.onboardingStatus"),
            content: (
              <OnboardingStatusKPI
                value={contract.onboardingStatus}
                valueVariant={valueVariant}
              />
            )
          }
        ]
      : []),
    {
      key: "renewalBook",
      subtitle: t("contracts.kpis.renewalBook"),
      content: (
        <RenewalBookKPI
          value={contract.percentageFromCsmRenewalBook}
          valueVariant={valueVariant}
        />
      )
    },
    {
      key: "nextRenewal",
      subtitle: t("contracts.kpis.nextRenewal"),
      content: (
        <NextRenewalKPI
          value={contract.nextRenewal}
          valueVariant={valueVariant}
        />
      )
    }
  ];

  return (
    <KPIsContainer className={containerClassName}>
      {map(kpis, ({ key, subtitle, content }) => (
        <div key={key} className="flex flex-col gap-1 p-3 min-w-0">
          <div className="flex items-center gap-1 h-12 min-w-0">{content}</div>
          <Text variant="bodySecondary">{subtitle}</Text>
        </div>
      ))}
    </KPIsContainer>
  );
};

export default ContractKPIs;
